/**
 * THE white-label switch point. `ACTIVE_BRAND_NAME` is the one line an operator edits to re-skin the whole product;
 * everything else in the tree reads `ACTIVE_BRAND` and re-skins with it.
 *
 * SELECTION IS BUILD-TIME, NOT RUNTIME, and that is a decision rather than an omission (operator adjudication
 * 2026-08-20). It follows the idiom this repo already uses for the wrangler fleet: per-brand ARTIFACTS are generated
 * from per-brand leaves and the selection is a const a build flips, never an env read. An env-driven selector would be
 * strictly worse here on three counts a reviewer cannot see at the call site:
 *  - `next/font` loaders and Tailwind token emission are COMPILE-TIME transforms, so the font and colour halves of a
 *    brand can never follow a runtime variable anyway -- an env switch would re-skin the strings and leave the type and
 *    palette on the incumbent brand, which is worse than not switching at all;
 *  - a `const/` leaf is read by compliance rules at module load (`QUAL-BRAND-GLOBAL-PREFIX-HARDCODED-001` composes its
 *    matcher from `ACTIVE_BRAND.globalNamespacePrefix`), and a detector whose verdict depends on the scanning host's
 *    environment is not a detector;
 *  - an absent or misspelled env var would fall THROUGH to a default brand silently -- the same invisible-fallback
 *    failure the required-member rule on `BrandIdentity` exists to prevent.
 *
 * ONE ENTRY TODAY IS THE POINT, NOT A PLACEHOLDER. The registry is keyed by `BrandName`, so adding a second brand is a
 * record plus a key and the compiler then DEMANDS the row -- `Record<BrandName, BrandIdentity>` cannot be satisfied by
 * a partial map, so a half-added brand fails the build instead of resolving `undefined` at a call site.
 */

import { FEDERA_BRAND } from './brand.ts';
import type { BrandIdentity } from './brand.ts';

/** Every brand this codebase can be built as. The value is the build-time selector token, lowercase and url-safe. */
export const BRAND_NAMES = {
	FEDERA: 'federa',
} as const;

/** The registry's key type. NOT exported: every reader of it is in this file (the `Record` key below and the
 *  `ACTIVE_BRAND_NAME` annotation). The sibling registries that must also be total over the brand set re-derive it from
 *  the exported `BRAND_NAMES` in one line rather than importing the alias, so the VALUE record stays the single thing a
 *  new brand is added to. Consumers read `ACTIVE_BRAND`; nothing outside needs to name the selector's type. */
type BrandName = (typeof BRAND_NAMES)[keyof typeof BRAND_NAMES];

/** Name -> identity record. `Record<BrandName, ...>` is load-bearing: it makes a missing row a type error rather than an `undefined` lookup. */
export const BRAND_REGISTRY: Record<BrandName, BrandIdentity> = {
	[BRAND_NAMES.FEDERA]: FEDERA_BRAND,
};

/**
 * THE SWITCH, and it is the ONLY line in the tree that names the brand a checkout builds. Change it (and rebuild) to
 * ship a different brand.
 *
 * EVERY OTHER BRAND-KEYED LEAF RESOLVES THROUGH IT, and that is enforced rather than asserted in prose:
 * `ACTIVE_BRAND_ASSETS` (`const/brand-assets.ts`) and the base.css generator's token-manifest pick both read THIS
 * const -- neither re-exports it under a second name -- and the design-token staleness surface needs no reading of it
 * at all: it is the generators' value-import closure (`DESIGN_TOKEN_GENERATOR_SEEDS`,
 * `const/derived-artifact-sources.ts`), which reaches every expression's manifest whichever brand is selected. And
 * `const/__tests__/brand-switch-single-valued.test.ts` re-points it to an unregistered slug and fails any leaf that
 * keeps the incumbent brand. Until 2026-08-20 four leaves each spelled the brand independently and two docstrings each
 * claimed to be the only one, so a brand switch was a silent four-line edit -- a partial one re-skinned the strings and
 * left the imagery, the artifact paths and the wordmark behind.
 *
 * A LEAF THAT *IS* A NAMED BRAND MUST NOT READ THIS SELECTION. The registries above pick BETWEEN brands and so resolve
 * through it; a record declaring one brand's own identity (`brand/federa/tokens/brand-manifest.ts`,
 * `brand/federa/assets/images.ts`, the federa imagery manifest in `const/brand-assets.ts`) reads the `BRAND_NAMES`
 * member or the identity record for the brand it IS. Reading the selection there inverts the switch: a second brand's
 * build emits THIS brand's values under the other brand's name, silently. The class is DETECTED rather than only
 * stated: `DS-BRAND-SLUG-OUTSIDE-NAME-RECORD-001` fires on an `ACTIVE_BRAND` / `ACTIVE_BRAND_NAME` /
 * `ACTIVE_BRAND_ASSETS` import specifier inside `brand/**`, which is where every named-brand leaf lives.
 *
 * Annotated `BrandName` rather than left to infer, so the value is checked against the registry's key set at THIS line
 * -- a typo is a compile error here instead of an `undefined` brand discovered at the first field read.
 */
export const ACTIVE_BRAND_NAME: BrandName = BRAND_NAMES.FEDERA;

/**
 * The active brand -- what every product surface reads. Resolved from the registry by {@link ACTIVE_BRAND_NAME}, never
 * assigned a brand record directly: an assignment would make the switch point two lines that can disagree.
 */
export const ACTIVE_BRAND: BrandIdentity = BRAND_REGISTRY[ACTIVE_BRAND_NAME];
